import { ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import { cloneElement, ReactElement, useEffect, useState } from "react";
import { purple, purpleLightHover } from "../../../theme/colors";

interface NavListItemProps {
  label: string;
  icon: ReactElement;
  linkTo: string;
  active: boolean;
  onClick?: () => void;
  horizontal?: boolean;
}

export default function NavListItem({
  label,
  icon,
  linkTo,
  active,
  onClick,
  horizontal = false,
}: NavListItemProps) {
  const coloredIcon = cloneElement(icon, {
    sx: { fontSize: 22, color: active ? "#FFFFFF" : "#5B4A5A" },
  });

  // Brief highlight pulse whenever this item newly becomes the active one
  // (e.g. jumping here from the navbar search) — makes the destination
  // obvious on top of the smooth scroll, not just a flat color swap.
  const [pulsing, setPulsing] = useState(false);
  useEffect(() => {
    if (!active) return;
    setPulsing(true);
    const timeout = setTimeout(() => setPulsing(false), 500);
    return () => clearTimeout(timeout);
  }, [active]);

  const button = (
    <ListItemButton
      component={Link}
      to={linkTo}
      onClick={onClick}
      data-active={active}
      disableRipple={active}
      sx={{
        flexDirection: horizontal ? "row" : "column",
        alignItems: "center",
        justifyContent: horizontal ? "flex-start" : "center",
        gap: horizontal ? 1.5 : 0.5,
        width: "100%",
        py: horizontal ? 1.5 : 1.25,
        px: horizontal ? 2 : undefined,
        bgcolor: active ? purple : "transparent",
        borderRadius: 3,
        mt: 0.75,
        mb: 0.75,
        scrollMarginBlock: 16,
        transition: "background-color 0.18s ease, transform 0.15s ease",
        "@keyframes navItemPulse": {
          "0%": { transform: "scale(1)", boxShadow: `0 0 0 0 ${purple}55` },
          "40%": { transform: "scale(1.05)", boxShadow: `0 0 0 8px ${purple}00` },
          "100%": { transform: "scale(1)", boxShadow: `0 0 0 0 ${purple}00` },
        },
        animation: pulsing ? "navItemPulse 0.5s ease" : "none",
        "&:hover": {
          bgcolor: active ? purple : purpleLightHover,
          transform: horizontal ? "none" : "translateY(-1px)",
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 0 }}>{coloredIcon}</ListItemIcon>
      <ListItemText
        primary={horizontal ? label : undefined}
        secondary={horizontal ? undefined : label}
        primaryTypographyProps={{
          fontSize: 14.5,
          lineHeight: 1.25,
          color: active ? "#FFFFFF" : "#5B4A5A",
          fontWeight: active ? 700 : 500,
        }}
        secondaryTypographyProps={{
          align: "center",
          fontSize: 11.5,
          lineHeight: 1.25,
          color: active ? "#FFFFFF" : "#5B4A5A",
          fontWeight: active ? 700 : 500,
        }}
      />
    </ListItemButton>
  );

  return horizontal ? button : (
    <Tooltip title={label} placement="right" arrow>
      {button}
    </Tooltip>
  );
}
